import { useEffect, useState } from 'react';
import axiosNest from '../../api/axiosNest';

/**
 * Korekty paliwowe — odpowiada `/fuel-surcharges` w API (makieta back
 * office v2, fala B, B4; Odpowiedzi CS 20.10.2026 pkt 4; decyzja D8 z
 * 20.10.2026; przełącznik `fuelSurcharges`, S-21.10-05). Kartoteka rodzajów,
 * wartości i przypisań kontrahentów, wgranie pliku od CS i informacja na
 * zleceniu — **bez wpływu na marżę i koszt**.
 */

/** Przełącznik funkcji — klucz z rejestru API (dane, nie przemianowujemy). */
export const FEATURE_FUEL_SURCHARGES = 'fuelSurcharges';

// ── Słowniki (wartości to dane z API; nazwy jak w `fuel-surcharges.ts`) ──

export const AUTHOR_LABELS: Record<string, string> = {
  marathon: 'Marathon',
  customer: 'klient',
};

export const PERIOD_LABELS: Record<string, string> = {
  week: 'tydzień',
  two_weeks: 'dwa tygodnie',
  month: 'miesiąc',
  quarter: 'kwartał',
};

export const BASIS_LABELS: Record<string, string> = {
  loading: 'wg daty załadunku',
  unloading: 'wg daty rozładunku',
};

export const SCOPE_LABELS: Record<string, string> = {
  customer_author: 'klient-autor (korekta klienta)',
  general: 'zbiór ogólny klientów',
  exception: 'wyjątek (indywidualnie)',
};

export const UNIT_LABELS: Record<string, string> = {
  percent: 'procent',
  amount: 'kwota',
};

/** Pusta wartość słownika = nieustalone (CS jeszcze nie wskazał). */
export const NOT_SET = 'nieustalony';

export const dictionaryLabel = (labels: Record<string, string>, v: string | null | undefined): string =>
  v ? (labels[v] ?? v) : NOT_SET;

/** Wybór do formularza: pusty wiersz („nieustalony”) i wartości słownika. */
export const choicesOf = (labels: Record<string, string>) => [
  { id: '', name: NOT_SET },
  ...Object.entries(labels).map(([id, name]) => ({ id, name })),
];

/** Pola historii dla operatora — klucze jak w API (`FIELD_LABELS`). */
export const FIELD_LABELS: Record<string, string> = {
  created: 'dodanie',
  withdrawn: 'wycofanie',
  name: 'nazwa',
  author: 'autor',
  period: 'okres',
  basis: 'moment naliczenia',
  scope: 'zakres',
  notes: 'uwagi',
  active: 'w użyciu',
  sortOrder: 'kolejność',
  validTo: 'data końca',
  note: 'uwagi',
};

export const ENTITY_LABELS: Record<string, string> = {
  type: 'rodzaj',
  value: 'wartość',
  assignment: 'przypisanie',
};

export const SOURCE_LABELS: Record<string, string> = {
  manual: 'ręcznie',
  file: 'z pliku od CS',
};

// ── Kształty odpowiedzi ──────────────────────────────────────────────

export interface FuelSurchargeType {
  id: number;
  code: string;
  name: string;
  author: string | null;
  period: string | null;
  basis: string | null;
  scope: string | null;
  notes: string | null;
  active: boolean;
  sortOrder: number;
  updatedAt?: string;
  updatedByName?: string | null;
  assignmentCount?: number;
  valueCount?: number;
}

export interface FuelSurchargeValue {
  id: number;
  typeId: number;
  validFrom: string;
  validTo: string;
  value: string;
  unit: string;
  currency: string | null;
  text: string;
  source: string;
  note: string | null;
  createdAt: string;
  createdByName: string | null;
  withdrawnAt: string | null;
  withdrawnByName: string | null;
  withdrawnReason: string | null;
  periodNote: string | null;
}

export interface FuelSurchargeAssignment {
  id: number;
  contractor: { id: number; legalName: string; tmsCustomerId: number | null; taxId: string | null };
  type: { id: number; code: string; name: string };
  validFrom: string;
  validTo: string | null;
  source: string;
  importId: number | null;
  note: string | null;
  createdAt: string;
  createdByName: string | null;
  withdrawnAt: string | null;
  withdrawnByName: string | null;
  withdrawnReason: string | null;
}

export interface FuelSurchargeChange {
  id: number;
  entity: string;
  entityId: number | null;
  typeId: number | null;
  contractorId: number | null;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  reason: string;
  changedByName: string | null;
  changedAt: string;
}

export type PlannedStatus = 'new' | 'change' | 'unchanged' | 'error';

export interface PlannedRow {
  rowNo: number;
  file: { tmsId: string | null; taxId: string | null; name: string | null; type: string | null; from: string | null; to: string | null };
  status: PlannedStatus;
  contractor: { id: number; legalName: string } | null;
  type: { id: number; code: string; name: string } | null;
  validFrom: string | null;
  validTo: string | null;
  endsPrevious: { assignmentId: number; typeName: string; validFrom: string; validTo: string } | null;
  problems: string[];
}

export interface ImportResult {
  dryRun: boolean;
  importId?: number;
  applied?: number;
  rows: PlannedRow[];
  summary: Record<PlannedStatus, number> & { total: number };
  unknownContractors: string[];
  unknownTypes: string[];
  planHash: string;
}

export interface FuelSurchargeImport {
  id: number;
  fileName: string;
  rowsTotal: number;
  applied: number;
  unchanged: number;
  errors: number;
  reason: string;
  createdAt: string;
  createdByName: string | null;
}

export interface ApplicableSurcharge {
  assignmentId: number;
  typeId: number;
  typeCode: string;
  typeName: string;
  period: string | null;
  basis: string | null;
  assignmentFrom: string;
  assignmentTo: string | null;
  basisDay: string | null;
  value: { id: number; value: string; unit: string; currency: string | null; validFrom: string; validTo: string; source: string; text: string } | null;
  valueNote: string | null;
  text: string;
}

export interface SurchargeInfo {
  contractor: { id: number; legalName: string } | null;
  /**
   * Skąd kontrahent, gdy nie z wiersza banku — prezlecenie bez wiersza
   * z kontrahentem wybranym z kartoteki (integracja fali B, 21.10.2026).
   */
  contractorSource?: string | null;
  loadingDay: string | null;
  unloadingDay: string | null;
  surcharges: ApplicableSurcharge[];
  note: string | null;
  marginNote: string;
}

export const PLANNED_STATUS_LABELS: Record<PlannedStatus, string> = {
  new: 'nowe przypisanie',
  change: 'zmiana korekty',
  unchanged: 'bez zmian',
  error: 'błąd',
};

// ── Zapytania ─────────────────────────────────────────────────────────

const BASE = '/fuel-surcharges';

const query = (params: Record<string, string | number | boolean | undefined>) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  return entries.length ? `?${entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')}` : '';
};

export async function fetchTypes(): Promise<FuelSurchargeType[]> {
  const { data } = await axiosNest.get<FuelSurchargeType[]>(`${BASE}/types`);
  return Array.isArray(data) ? data : [];
}

export async function createType(input: Partial<FuelSurchargeType> & { reason: string }) {
  const { data } = await axiosNest.post(`${BASE}/types`, input);
  return data as FuelSurchargeType;
}

export async function updateType(id: number, input: Partial<FuelSurchargeType> & { reason: string }) {
  const { data } = await axiosNest.patch(`${BASE}/types/${id}`, input);
  return data as FuelSurchargeType;
}

export async function fetchValues(typeId: number): Promise<FuelSurchargeValue[]> {
  const { data } = await axiosNest.get<{ values: FuelSurchargeValue[] }>(`${BASE}/types/${typeId}/values`);
  return Array.isArray(data?.values) ? data.values : [];
}

export async function createValue(
  typeId: number,
  input: { validFrom: string; validTo: string; value: string; unit: string; currency?: string; source: string; note?: string },
) {
  const { data } = await axiosNest.post(`${BASE}/types/${typeId}/values`, input);
  return data as { id: number; text: string; periodNote: string | null };
}

export async function withdrawValue(id: number, reason: string) {
  const { data } = await axiosNest.post(`${BASE}/values/${id}/withdraw`, { reason });
  return data;
}

export async function fetchAssignments(filter: {
  contractorId?: number;
  typeId?: number;
  withdrawn?: boolean;
}): Promise<FuelSurchargeAssignment[]> {
  const { data } = await axiosNest.get<FuelSurchargeAssignment[]>(
    `${BASE}/assignments${query({ contractorId: filter.contractorId, typeId: filter.typeId, withdrawn: filter.withdrawn || undefined })}`,
  );
  return Array.isArray(data) ? data : [];
}

export async function createAssignment(input: {
  contractorId: number;
  typeId: number;
  validFrom: string;
  validTo?: string | null;
  note?: string;
  reason: string;
  endPrevious?: boolean;
}) {
  const { data } = await axiosNest.post(`${BASE}/assignments`, input);
  return data as { assignment: FuelSurchargeAssignment; endedPrevious: { id: number; validTo: string } | null };
}

export async function updateAssignment(id: number, input: { validTo?: string | null; note?: string | null; reason: string }) {
  const { data } = await axiosNest.patch(`${BASE}/assignments/${id}`, input);
  return data as FuelSurchargeAssignment;
}

export async function withdrawAssignment(id: number, reason: string) {
  const { data } = await axiosNest.post(`${BASE}/assignments/${id}/withdraw`, { reason });
  return data;
}

export async function fetchHistory(filter: { contractorId?: number; typeId?: number } = {}): Promise<FuelSurchargeChange[]> {
  const { data } = await axiosNest.get<FuelSurchargeChange[]>(`${BASE}/history${query(filter)}`);
  return Array.isArray(data) ? data : [];
}

export async function fetchImports(): Promise<FuelSurchargeImport[]> {
  const { data } = await axiosNest.get<FuelSurchargeImport[]>(`${BASE}/imports`);
  return Array.isArray(data) ? data : [];
}

/**
 * Plik od CS: podgląd (`dryRun`) albo potwierdzenie — ten sam plik, odcisk
 * planu z podglądu i powód. Serwer odmawia potwierdzenia innego planu (409).
 */
export async function importFile(
  file: File,
  options: { dryRun: boolean; planHash?: string; reason?: string },
): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  if (options.planHash) form.append('planHash', options.planHash);
  if (options.reason) form.append('reason', options.reason);
  const { data } = await axiosNest.post<ImportResult>(
    `${BASE}/assignments/import${options.dryRun ? '?dryRun=true' : ''}`,
    form,
  );
  return data;
}

/** Wyszukanie kontrahenta do przypisania — lista kartoteki (`q` po nazwie i NIP). */
export async function searchContractors(q: string): Promise<Array<{ id: number; legalName: string; tmsCustomerId: number | null; taxId: string | null }>> {
  const filter = encodeURIComponent(JSON.stringify({ q }));
  const { data } = await axiosNest.get(`/contractors?filter=${filter}&range=${encodeURIComponent('[0,19]')}&sort=${encodeURIComponent('["legalName","ASC"]')}`);
  return Array.isArray(data) ? data : [];
}

/** Odmowa 409 z kodem (np. `previous` — trzeba zgody na zakończenie poprzedniej korekty). */
export function conflictCode(err: unknown): string | null {
  const code = (err as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return code?.status === 409 && typeof code.data?.code === 'string' ? code.data.code : null;
}

/**
 * Korekta obowiązująca dla zlecenia albo prezlecenia. `enabled = false` —
 * bez zapytania (przełącznik wyłączony: API odpowiada 404). `undefined` —
 * wczytuje się; `null` — błąd odczytu (sekcja mówi, że nie wiadomo).
 * `refreshKey` — zmiana wczytuje ponownie (okno kontroli po zmianie
 * kontrahenta, 21.10.2026).
 */
export function useSurchargeInfo(
  kind: 'order' | 'pre-order',
  id: number | string | undefined,
  enabled: boolean,
  refreshKey?: unknown,
): SurchargeInfo | null | undefined {
  const [info, setInfo] = useState<SurchargeInfo | null | undefined>(undefined);
  useEffect(() => {
    if (!enabled || id === undefined || id === null || id === '') return undefined;
    let active = true;
    setInfo(undefined);
    const load = async () => {
      try {
        const res = await axiosNest.get<SurchargeInfo>(`${BASE}/${kind}/${id}`);
        if (active) setInfo(res?.data ?? null);
      } catch {
        if (active) setInfo(null);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [kind, id, enabled, refreshKey]);
  return info;
}

/** Data `YYYY-MM-DD` → `23.10.2026` (data, bez strefy — klasa „data”). */
export function plDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** „od 01.10.2026 do 31.10.2026” albo „od 01.10.2026, bez daty końca”. */
export function rangeText(from: string, to: string | null): string {
  return to ? `od ${plDay(from)} do ${plDay(to)}` : `od ${plDay(from)}, bez daty końca`;
}

